import { useEffect, useMemo } from 'react'

import { withResourceRecords } from '@/shared/contexts'
import { useAppRouter } from '@/shared/hooks/useAppRouter'
import { useEventSlug } from '@/shared/hooks/useEventSlug'
import { useGamificationPages } from '@/shared/lib/api/gamification-pages'
import { sanitizeEmbedHtml, sanitizeHtml } from '@/shared/lib/html/sanitize'
import { PageHeader, Screen } from '@/shared/ui/Layout'
import { safeHref } from '@/shared/lib/url/safe-href'

import {
  buildPartnersListFilter,
  getPartnerCategoryIdsFromPage,
  getShowTopLikesFromPage,
  isPartnersModulePage
} from '@/features/custom-pages/lib/partnerPageFilters'
import { PartnersListScreen } from '@/features/partners/components/PartnersListScreen'
import { getConfig } from '@/features/partners/config'

function CustomPageShell({ title, children }) {
  return (
    <Screen>
      <Screen.Context>
        <PageHeader title={title} />
      </Screen.Context>
      <Screen.Content className="flex flex-col gap-3 pb-6 pt-2">{children}</Screen.Content>
    </Screen>
  )
}

const PartnersModuleList = withResourceRecords(PartnersListScreen, getConfig)

const PartnersModuleContent = ({ page, digitalProductId }) => {
  const categoryIdsKey = useMemo(
    () => getPartnerCategoryIdsFromPage(page).join(','),
    [page]
  )
  const categoryIds = useMemo(
    () =>
      categoryIdsKey
        ? categoryIdsKey.split(',').map(id => Number(id))
        : [],
    [categoryIdsKey]
  )
  const filter = useMemo(
    () => buildPartnersListFilter(categoryIds),
    [categoryIdsKey]
  )
  const showTopLikes = useMemo(
    () => getShowTopLikesFromPage(page),
    [page]
  )

  return (
    <PartnersModuleList
      digitalProductId={digitalProductId}
      filter={filter}
      title={page.title}
      showTopLikes={showTopLikes}
    />
  )
}

export const DigitalProductCustomPage = ({ slug }) => {
  const router = useAppRouter()
  const digitalProductId = useEventSlug()
  const { records: pages, isLoading } = useGamificationPages()

  const page = pages?.find(p => p.slug === slug) ?? null

  useEffect(() => {
    if (!isLoading && !page) {
      router.replace('/')
    }
  }, [isLoading, page, router])

  useEffect(() => {
    if (
      page?.content_kind === 'external_link' &&
      page.external_link &&
      typeof window !== 'undefined'
    ) {
      const href = safeHref(page.external_link)
      if (href) window.location.replace(href)
    }
  }, [page])

  if (isLoading) {
    return (
      <CustomPageShell title="Carregando...">
        <p className="text-gray-700">Carregando conteúdo...</p>
      </CustomPageShell>
    )
  }

  if (!page) return null

  if (page.content_kind === 'external_link') {
    return null
  }

  if (isPartnersModulePage(page)) {
    return (
      <PartnersModuleContent
        page={page}
        digitalProductId={digitalProductId}
      />
    )
  }

  const renderContent = () => {
    if (page.content_kind === 'text') {
      return (
        <div
          className="wysiwyg-content text-base text-gray-900"
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(page.text_content || '')
          }}
        />
      )
    }

    if (page.content_kind === 'embed_code') {
      return (
        <div
          className="overflow-hidden rounded-xl [&_iframe]:h-full [&_iframe]:w-full"
          dangerouslySetInnerHTML={{
            __html: sanitizeEmbedHtml(page.embed_code || '')
          }}
        />
      )
    }

    return null
  }

  return <CustomPageShell title={page.title}>{renderContent()}</CustomPageShell>
}
